import { useState, useEffect, useRef, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getCrmTasks, createTask, updateTask, deleteTask, getTeamMembers,
  getCrmTaskGroups, createTaskGroup, updateTaskGroup, deleteTaskGroup,
} from '../api/client'
import { formatDate } from '../utils/weeks'

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  todo: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-blue-100 text-blue-700',
  done: 'bg-emerald-100 text-emerald-700',
}
const PRIORITY_STYLES = {
  low: 'bg-gray-100 text-gray-500',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-red-100 text-red-600',
}

function isSoon(d) {
  if (!d) return false
  const diff = (new Date(d + 'T00:00:00') - new Date().setHours(0, 0, 0, 0)) / 86400000
  return diff >= 0 && diff <= 7
}
function isOverdue(d) {
  if (!d) return false
  return new Date(d + 'T00:00:00') < new Date().setHours(0, 0, 0, 0)
}

// ── Ghost input styles ────────────────────────────────────────────────────────
const ghostText = 'bg-transparent border border-transparent rounded px-1.5 py-1 w-full text-sm text-gray-900 hover:bg-white hover:border-gray-200 focus:bg-white focus:border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-100 transition-colors'
const ghostSelect = 'appearance-none cursor-pointer bg-transparent border border-transparent rounded px-1.5 py-0.5 text-sm text-gray-500 w-full hover:bg-white hover:border-gray-200 focus:bg-white focus:border-gray-300 focus:outline-none transition-colors'
const ghostDate = 'bg-transparent border border-transparent rounded px-1.5 py-0.5 text-xs hover:bg-white hover:border-gray-200 focus:bg-white focus:border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-100 transition-colors'
const cellInput = 'w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white'

// ── TaskRow ───────────────────────────────────────────────────────────────────
function TaskRow({ task, members, onSaved, onDelete, isDragging, onDragStart, onDragEnd, onDragEnterGroup, onDropOnGroup }) {
  const { t } = useTranslation('crm')
  const [name, setName] = useState(task.name)
  const [status, setStatus] = useState(task.status)
  const [priority, setPriority] = useState(task.priority)
  const [responsibleId, setResponsibleId] = useState(task.responsible_id ?? '')
  const [startDate, setStartDate] = useState(task.start_date ?? '')
  const [deadline, setDeadline] = useState(task.deadline ?? '')

  const STATUSES = [
    { value: 'todo', label: t('tasks.statusTodo') },
    { value: 'in_progress', label: t('tasks.statusInProgress') },
    { value: 'done', label: t('tasks.statusDone') },
  ]
  const PRIORITIES = [
    { value: 'low', label: t('tasks.priorityLow') },
    { value: 'medium', label: t('tasks.priorityMedium') },
    { value: 'high', label: t('tasks.priorityHigh') },
  ]

  useEffect(() => {
    setName(task.name); setStatus(task.status); setPriority(task.priority)
    setResponsibleId(task.responsible_id ?? ''); setStartDate(task.start_date ?? ''); setDeadline(task.deadline ?? '')
  }, [task.id, task.name, task.status, task.priority, task.responsible_id, task.start_date, task.deadline])

  const save = (patch) => updateTask(task.id, patch).then(onSaved).catch(() => {})

  const handleNameBlur = () => {
    const trimmed = name.trim()
    if (!trimmed) { setName(task.name); return }
    if (trimmed !== task.name) save({ name: trimmed })
  }

  const deadlineColor = deadline
    ? task.status === 'done' ? 'text-gray-400'
      : isOverdue(deadline) ? 'text-red-600'
      : isSoon(deadline)    ? 'text-amber-600'
      : 'text-gray-500'
    : 'text-gray-300'

  const dragProps = {
    draggable: true,
    onDragStart: e => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') { e.preventDefault(); return }
      e.dataTransfer.effectAllowed = 'move'
      onDragStart && onDragStart(task.id)
    },
    onDragEnd: () => onDragEnd && onDragEnd(),
    onDragOver: e => e.preventDefault(),
    onDragEnter: () => onDragEnterGroup && onDragEnterGroup(task.group_id ?? null),
    onDrop: e => { e.preventDefault(); onDropOnGroup && onDropOnGroup(task.group_id ?? null) },
  }

  return (
    <tr
      {...dragProps}
      className={`border-b border-gray-50 group hover:bg-gray-50/60 transition-colors last:border-b-0 ${isDragging ? 'opacity-40' : ''}`}
    >
      <td className="w-6 px-2 py-1.5 cursor-grab">
        <span className="text-gray-300 group-hover:text-gray-400 text-sm select-none transition-colors">⠿</span>
      </td>
      <td className="px-3 py-1.5">
        <input className={ghostText} value={name} onChange={e => setName(e.target.value)} onBlur={handleNameBlur} />
      </td>
      <td className="px-3 py-1.5">
        <select value={status} onChange={e => { setStatus(e.target.value); save({ status: e.target.value }) }}
          className={`appearance-none cursor-pointer rounded-full text-xs font-medium px-2.5 py-0.5 focus:outline-none hover:opacity-75 transition-opacity ${STATUS_STYLES[status]}`}>
          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </td>
      <td className="px-3 py-1.5">
        <select value={priority} onChange={e => { setPriority(e.target.value); save({ priority: e.target.value }) }}
          className={`appearance-none cursor-pointer rounded-full text-xs font-medium px-2.5 py-0.5 focus:outline-none hover:opacity-75 transition-opacity ${PRIORITY_STYLES[priority]}`}>
          {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </td>
      <td className="px-3 py-1.5">
        <select value={responsibleId} onChange={e => { setResponsibleId(e.target.value); save({ responsible_id: e.target.value ? Number(e.target.value) : null }) }} className={ghostSelect}>
          <option value="">—</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </td>
      <td className="px-3 py-1.5">
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
          onBlur={() => { if (startDate !== (task.start_date ?? '')) save({ start_date: startDate || null }) }}
          className={`${ghostDate} text-gray-500`} />
      </td>
      <td className="px-3 py-1.5">
        <input type="date" value={deadline} onChange={e => setDeadline(e.target.value)}
          onBlur={() => { if (deadline !== (task.deadline ?? '')) save({ deadline: deadline || null }) }}
          className={`${ghostDate} ${deadlineColor}`} />
      </td>
      <td className="px-3 py-1.5 text-right">
        <button onClick={() => onDelete(task)} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all text-xs">{t('tasks.deleteTaskBtn')}</button>
      </td>
    </tr>
  )
}

// ── NewTaskRow ────────────────────────────────────────────────────────────────
const EMPTY_TASK = { name: '', status: 'todo', priority: 'medium', responsible_id: '', start_date: '', deadline: '' }

function NewTaskRow({ members, groupId, nameRef, onSaved, onCancel }) {
  const { t } = useTranslation('crm')
  const [form, setForm] = useState(EMPTY_TASK)
  const [saving, setSaving] = useState(false)

  const STATUSES = [
    { value: 'todo', label: t('tasks.statusTodo') },
    { value: 'in_progress', label: t('tasks.statusInProgress') },
    { value: 'done', label: t('tasks.statusDone') },
  ]
  const PRIORITIES = [
    { value: 'low', label: t('tasks.priorityLow') },
    { value: 'medium', label: t('tasks.priorityMedium') },
    { value: 'high', label: t('tasks.priorityHigh') },
  ]

  const commit = async () => {
    if (!form.name.trim()) return
    setSaving(true)
    try {
      await createTask({
        ...form,
        context: 'crm',
        project_id: null,
        group_id: groupId ?? null,
        responsible_id: form.responsible_id ? Number(form.responsible_id) : null,
        start_date: form.start_date || null,
        deadline: form.deadline || null,
      })
      onSaved()
    } catch (_) {}
    setSaving(false)
  }

  return (
    <tr className="border-b border-gray-100 bg-blue-50/40">
      <td className="w-6 px-2 py-2"></td>
      <td className="px-3 py-2"><input ref={nameRef} className={cellInput} placeholder={t('tasks.taskNamePlaceholder')} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') onCancel() }} /></td>
      <td className="px-3 py-2"><select className={cellInput} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>{STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</select></td>
      <td className="px-3 py-2"><select className={cellInput} value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>{PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}</select></td>
      <td className="px-3 py-2"><select className={cellInput} value={form.responsible_id} onChange={e => setForm(f => ({ ...f, responsible_id: e.target.value }))}><option value="">{t('tasks.unassigned')}</option>{members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</select></td>
      <td className="px-3 py-2"><input type="date" className={cellInput} value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} /></td>
      <td className="px-3 py-2"><input type="date" className={cellInput} value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} /></td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <button onClick={commit} disabled={saving || !form.name.trim()} className="text-xs font-medium text-white bg-gray-900 px-2.5 py-1 rounded hover:bg-gray-700 disabled:opacity-40 mr-1.5">{saving ? '…' : t('tasks.addBtn')}</button>
        <button onClick={onCancel} className="text-xs text-gray-500 hover:text-gray-800">{t('common:actions.cancel')}</button>
      </td>
    </tr>
  )
}

// ── GroupHeaderRow ────────────────────────────────────────────────────────────
function GroupHeaderRow({ group, count, collapsed, onToggle, onRename, onDelete, onAddTask, isAddingTask, isDragOver, onDragEnter, onDragLeave, onDrop }) {
  const { t } = useTranslation('crm')
  const [renaming, setRenaming] = useState(false)
  const [name, setName] = useState(group.name)

  useEffect(() => setName(group.name), [group.name])

  const commitRename = () => {
    const trimmed = name.trim()
    if (!trimmed) { setName(group.name); setRenaming(false); return }
    if (trimmed !== group.name) onRename(trimmed)
    setRenaming(false)
  }

  return (
    <tr
      className={`border-y group/grow transition-colors ${isDragOver ? 'bg-blue-50 border-blue-300' : 'bg-gray-50/70 border-gray-100'}`}
      onDragEnter={onDragEnter}
      onDragOver={e => e.preventDefault()}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) onDragLeave && onDragLeave() }}
      onDrop={e => { e.preventDefault(); onDrop && onDrop() }}
    >
      <td colSpan={8} className="px-3 py-2">
        <div className="flex items-center gap-2">
          <button onClick={onToggle} className="text-gray-400 hover:text-gray-700 w-3 text-[10px] leading-none shrink-0">
            {collapsed ? '▶' : '▼'}
          </button>
          {renaming ? (
            <input
              autoFocus
              value={name}
              onChange={e => setName(e.target.value)}
              onBlur={commitRename}
              onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') { setName(group.name); setRenaming(false) } }}
              className="text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-gray-300 min-w-0"
            />
          ) : (
            <span className="text-sm font-semibold text-gray-700">{group.name}</span>
          )}
          <span className="text-xs text-gray-400 shrink-0">({count})</span>
          {!renaming && (
            <div className="hidden group-hover/grow:flex items-center gap-2 ml-1">
              <button onClick={() => setRenaming(true)} className="text-xs text-gray-400 hover:text-gray-700">{t('tasks.renameBtn')}</button>
              <button onClick={onDelete} className="text-xs text-gray-400 hover:text-red-500">{t('tasks.deleteGroupBtn')}</button>
            </div>
          )}
          {!isAddingTask && (
            <button onClick={onAddTask} className="ml-auto text-xs text-gray-400 hover:text-gray-700 hover:underline shrink-0">{t('tasks.addTaskBtn')}</button>
          )}
        </div>
      </td>
    </tr>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function CRMTasks() {
  const { t } = useTranslation(['crm', 'common'])
  const [tasks, setTasks] = useState([])
  const [groups, setGroups] = useState([])
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)

  // addingTaskGroupId: null = none | 'ungrouped' | number (group id)
  const [addingTaskGroupId, setAddingTaskGroupId] = useState(null)
  const taskNameRef = useRef(null)

  const [collapsedGroups, setCollapsedGroups] = useState({})

  const [draggingTaskId, setDraggingTaskId] = useState(null)
  const [dragOverGroupId, setDragOverGroupId] = useState(null)

  const [addingGroup, setAddingGroup] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const newGroupRef = useRef(null)

  const load = async () => {
    setLoading(true)
    const [ts, grps, mem] = await Promise.all([
      getCrmTasks(), getCrmTaskGroups(), getTeamMembers({ active: true }),
    ])
    setTasks(ts); setGroups(grps); setMembers(mem)
    setLoading(false)
  }

  useEffect(() => { load() }, [])
  useEffect(() => { if (addingTaskGroupId !== null) taskNameRef.current?.focus() }, [addingTaskGroupId])
  useEffect(() => { if (addingGroup) newGroupRef.current?.focus() }, [addingGroup])

  const handleTaskDrop = async (targetGroupId) => {
    const tid = draggingTaskId
    setDraggingTaskId(null)
    setDragOverGroupId(null)
    if (tid == null) return
    const taskObj = tasks.find(task => task.id === tid)
    if (!taskObj) return
    const prevGroupId = taskObj.group_id ?? null
    if (prevGroupId === targetGroupId) return
    setTasks(prev => prev.map(task => task.id === tid ? { ...task, group_id: targetGroupId } : task))
    try {
      await updateTask(tid, { group_id: targetGroupId })
    } catch {
      setTasks(prev => prev.map(task => task.id === tid ? { ...task, group_id: prevGroupId } : task))
    }
  }

  const handleTaskSaved = (updated) => setTasks(prev => prev.map(task => task.id === updated.id ? updated : task))
  const handleTaskAdded = async () => { setTasks(await getCrmTasks()); setAddingTaskGroupId(null) }
  const handleDeleteTask = async (taskToDelete) => {
    if (!confirm(t('tasks.deleteTaskConfirm', { name: taskToDelete.name }))) return
    await deleteTask(taskToDelete.id)
    setTasks(ts => ts.filter(x => x.id !== taskToDelete.id))
  }

  const handleGroupRename = async (groupId, name) => {
    const updated = await updateTaskGroup(groupId, { name })
    setGroups(prev => prev.map(g => g.id === groupId ? updated : g))
  }
  const handleGroupDelete = async (group) => {
    if (!confirm(t('tasks.deleteGroupConfirm', { name: group.name }))) return
    await deleteTaskGroup(group.id)
    setGroups(prev => prev.filter(g => g.id !== group.id))
    setTasks(prev => prev.map(task => task.group_id === group.id ? { ...task, group_id: null } : task))
  }
  const commitAddGroup = async () => {
    const trimmed = newGroupName.trim()
    if (!trimmed) return
    const group = await createTaskGroup({ context: 'crm', project_id: null, name: trimmed })
    setGroups(prev => [...prev, group])
    setNewGroupName('')
    setAddingGroup(false)
  }
  const cancelAddGroup = () => { setNewGroupName(''); setAddingGroup(false) }

  const ungroupedTasks = tasks.filter(task => !task.group_id)
  const hasGroups = groups.length > 0

  const TableHead = () => (
    <thead>
      <tr className="bg-gray-50 border-b border-gray-100">
        <th className="w-6 px-2 py-2.5"></th>
        <th className="text-left px-3 py-2.5 font-semibold text-gray-600 text-xs">{t('tasks.colName')}</th>
        <th className="text-left px-3 py-2.5 font-semibold text-gray-600 text-xs">{t('tasks.colStatus')}</th>
        <th className="text-left px-3 py-2.5 font-semibold text-gray-600 text-xs">{t('tasks.colPriority')}</th>
        <th className="text-left px-3 py-2.5 font-semibold text-gray-600 text-xs">{t('tasks.colResponsible')}</th>
        <th className="text-left px-3 py-2.5 font-semibold text-gray-600 text-xs">{t('tasks.colStart')}</th>
        <th className="text-left px-3 py-2.5 font-semibold text-gray-600 text-xs">{t('tasks.colDeadline')}</th>
        <th className="px-3 py-2.5"></th>
      </tr>
    </thead>
  )

  return (
    <div className="p-8">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="min-w-0">
          <h1 className="text-[28px] font-bold text-gray-900 tracking-tight leading-tight">{t('tasks.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('tasks.taskCount', { count: tasks.length })}</p>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-400 text-sm">{t('common:actions.loading')}</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-700">{t('tasks.sectionLabel')}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setAddingGroup(true); setAddingTaskGroupId(null) }}
                disabled={addingGroup}
                className="text-xs text-gray-600 border border-gray-200 px-2.5 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                {t('tasks.addGroupBtn')}
              </button>
              <button
                onClick={() => setAddingTaskGroupId('ungrouped')}
                disabled={addingTaskGroupId !== null}
                className="bg-gray-900 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-700 disabled:opacity-40 transition-colors"
              >
                {t('tasks.newTaskBtn')}
              </button>
            </div>
          </div>

          {tasks.length === 0 && groups.length === 0 && addingTaskGroupId === null && !addingGroup ? (
            <p className="text-sm text-gray-400 px-5 py-4">{t('tasks.emptyState')}</p>
          ) : (
            <table className="w-full text-sm">
              <TableHead />
              <tbody>
                {!hasGroups ? (
                  <>
                    {tasks.map(task => (
                      <TaskRow key={task.id} task={task} members={members} onSaved={handleTaskSaved} onDelete={handleDeleteTask}
                        isDragging={draggingTaskId === task.id}
                        onDragStart={setDraggingTaskId}
                        onDragEnd={() => { setDraggingTaskId(null); setDragOverGroupId(null) }}
                      />
                    ))}
                    {addingTaskGroupId === 'ungrouped' && (
                      <NewTaskRow members={members} groupId={null} nameRef={taskNameRef} onSaved={handleTaskAdded} onCancel={() => setAddingTaskGroupId(null)} />
                    )}
                  </>
                ) : (
                  <>
                    {groups.map(group => {
                      const groupTasks = tasks.filter(task => task.group_id === group.id)
                      const isCollapsed = !!collapsedGroups[group.id]
                      return (
                        <Fragment key={group.id}>
                          <GroupHeaderRow
                            group={group}
                            count={groupTasks.length}
                            collapsed={isCollapsed}
                            onToggle={() => setCollapsedGroups(prev => ({ ...prev, [group.id]: !prev[group.id] }))}
                            onRename={(name) => handleGroupRename(group.id, name)}
                            onDelete={() => handleGroupDelete(group)}
                            onAddTask={() => { setAddingTaskGroupId(group.id); setCollapsedGroups(prev => ({ ...prev, [group.id]: false })) }}
                            isAddingTask={addingTaskGroupId === group.id}
                            isDragOver={dragOverGroupId === group.id}
                            onDragEnter={() => draggingTaskId != null && setDragOverGroupId(group.id)}
                            onDragLeave={() => setDragOverGroupId(null)}
                            onDrop={() => handleTaskDrop(group.id)}
                          />
                          {!isCollapsed && (
                            <>
                              {groupTasks.map(task => (
                                <TaskRow key={task.id} task={task} members={members} onSaved={handleTaskSaved} onDelete={handleDeleteTask}
                                  isDragging={draggingTaskId === task.id}
                                  onDragStart={setDraggingTaskId}
                                  onDragEnd={() => { setDraggingTaskId(null); setDragOverGroupId(null) }}
                                  onDragEnterGroup={gid => draggingTaskId != null && setDragOverGroupId(gid)}
                                  onDropOnGroup={handleTaskDrop}
                                />
                              ))}
                              {addingTaskGroupId === group.id && (
                                <NewTaskRow members={members} groupId={group.id} nameRef={taskNameRef} onSaved={handleTaskAdded} onCancel={() => setAddingTaskGroupId(null)} />
                              )}
                            </>
                          )}
                        </Fragment>
                      )
                    })}

                    {(ungroupedTasks.length > 0 || addingTaskGroupId === 'ungrouped') && (
                      <Fragment>
                        <tr
                          className={`border-y transition-colors ${dragOverGroupId === 'ungrouped' ? 'bg-blue-50 border-blue-300' : 'bg-gray-50/70 border-gray-100'}`}
                          onDragEnter={() => draggingTaskId != null && setDragOverGroupId('ungrouped')}
                          onDragOver={e => e.preventDefault()}
                          onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverGroupId(null) }}
                          onDrop={e => { e.preventDefault(); handleTaskDrop(null) }}
                        >
                          <td colSpan={8} className="px-3 py-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => setCollapsedGroups(prev => ({ ...prev, _ungrouped: !prev._ungrouped }))}
                                  className="text-gray-400 hover:text-gray-700 w-3 text-[10px] leading-none shrink-0"
                                >
                                  {collapsedGroups._ungrouped ? '▶' : '▼'}
                                </button>
                                <span className="text-sm font-semibold text-gray-500">{t('tasks.ungrouped')}</span>
                                <span className="text-xs text-gray-400">({ungroupedTasks.length})</span>
                              </div>
                              {addingTaskGroupId !== 'ungrouped' && (
                                <button onClick={() => setAddingTaskGroupId('ungrouped')} className="text-xs text-gray-400 hover:text-gray-700 hover:underline">{t('tasks.addTaskBtn')}</button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {!collapsedGroups._ungrouped && (
                          <>
                            {ungroupedTasks.map(task => (
                              <TaskRow key={task.id} task={task} members={members} onSaved={handleTaskSaved} onDelete={handleDeleteTask}
                                isDragging={draggingTaskId === task.id}
                                onDragStart={setDraggingTaskId}
                                onDragEnd={() => { setDraggingTaskId(null); setDragOverGroupId(null) }}
                                onDragEnterGroup={gid => draggingTaskId != null && setDragOverGroupId(gid)}
                                onDropOnGroup={handleTaskDrop}
                              />
                            ))}
                            {addingTaskGroupId === 'ungrouped' && (
                              <NewTaskRow members={members} groupId={null} nameRef={taskNameRef} onSaved={handleTaskAdded} onCancel={() => setAddingTaskGroupId(null)} />
                            )}
                          </>
                        )}
                      </Fragment>
                    )}
                  </>
                )}

                {addingGroup && (
                  <tr className="border-y border-gray-100 bg-gray-50/70">
                    <td colSpan={8} className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <input
                          ref={newGroupRef}
                          value={newGroupName}
                          onChange={e => setNewGroupName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') commitAddGroup(); if (e.key === 'Escape') cancelAddGroup() }}
                          placeholder={t('tasks.groupNamePlaceholder')}
                          className="text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-gray-300 min-w-0 flex-1 max-w-xs"
                        />
                        <button onClick={commitAddGroup} disabled={!newGroupName.trim()} className="text-xs font-medium text-white bg-gray-900 px-2.5 py-1 rounded hover:bg-gray-700 disabled:opacity-40">{t('tasks.addBtn')}</button>
                        <button onClick={cancelAddGroup} className="text-xs text-gray-500 hover:text-gray-800">{t('common:actions.cancel')}</button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}
